import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Printer, FileText } from "lucide-react";

const OPTIONS = [
  { key: "prewriting", label: "Pre-Writing Strokes", rows: ["curves", "loops", "waves", "slants"] },
  { key: "lowercase", label: "Lowercase Letters", rows: ["a b c d e", "f g h i j", "k l m n o", "p q r s t", "u v w x y z"] },
  { key: "uppercase", label: "Uppercase Letters", rows: ["A B C D E", "F G H I J", "K L M N O"] },
  { key: "connections", label: "Connections", rows: ["an", "th", "ch", "be", "or"] },
  { key: "words", label: "Words", rows: ["cat", "dog", "home", "friend"] },
  { key: "sentences", label: "Sentences", rows: ["I like my dog.", "The sun is bright."] },
];

function GuideRow({ text }) {
  return (
    <div style={{ margin: "14px 0" }}>
      <div style={{ fontFamily: "Caveat, cursive", fontSize: 34, color: "#0D9488", lineHeight: 1 }}>{text}</div>
      <div style={{ position: "relative", height: 60, marginTop: 4 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, borderTop: "1px solid #DC2626" }} />
        <div style={{ position: "absolute", top: 30, left: 0, right: 0, borderTop: "1px dashed #0284C7" }} />
        <div style={{ position: "absolute", top: 60, left: 0, right: 0, borderTop: "1.5px solid #15803D" }} />
      </div>
    </div>
  );
}

export default function WorksheetGenerator() {
  const { user } = useAuth();
  const [selected, setSelected] = useState(["lowercase"]);
  const [students, setStudents] = useState([]);
  const [studentName, setStudentName] = useState("");

  useEffect(() => { api.listStudents().then(setStudents).catch(() => {}); }, []);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const rows = OPTIONS.filter((o) => selected.includes(o.key)).flatMap((o) => o.rows.map((r) => ({ group: o.label, text: r })));

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="print:hidden">
        <div className="text-xs uppercase tracking-widest font-bold text-[#0284C7]">{user?.role === "parent" ? "Parent" : "Teacher"} Tools</div>
        <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Printable Practice Sheet</h1>
        <p className="mt-1 font-body text-[#6B7280]">Pick what to include, then print. No account info is added to the sheet.</p>

        <div className="mt-5 grid gap-2 sm:grid-cols-3">
          {OPTIONS.map((o) => (
            <button key={o.key} onClick={() => toggle(o.key)} data-testid={`ws-opt-${o.key}`}
              className={`rounded-2xl border-2 px-4 py-3 text-left font-bold transition-all ${selected.includes(o.key) ? "border-[#0D9488] bg-[#0D9488]/5 text-[#0D9488]" : "border-[#E5DECF] text-[#6B7280] hover:bg-[#F2ECE1]"}`}>
              {o.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select value={studentName} onChange={(e) => setStudentName(e.target.value)} className="rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-2.5 font-body">
            <option value="">No student name</option>
            {students.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <button onClick={() => window.print()} data-testid="print-worksheet-button" className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3 font-bold text-white shadow-md hover:bg-[#0B7F74] active:translate-y-0.5">
            <Printer size={18} /> Print Worksheet
          </button>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="mt-8 rounded-2xl border border-[#E5DECF] bg-white p-8 print:border-0 print:p-0" data-testid="worksheet-preview">
        <div className="flex items-center justify-between border-b border-[#E5DECF] pb-3">
          <div className="flex items-center gap-2 font-heading text-xl font-extrabold text-[#1F2937]"><FileText size={22} /> Cursive Practice</div>
          <div className="text-sm text-[#6B7280]">Name: {studentName || "________"} &nbsp; Date: ____________</div>
        </div>
        {rows.length === 0 ? (
          <p className="mt-6 text-center text-[#94A3B8]">Select at least one section above.</p>
        ) : (
          rows.map((r, i) => <GuideRow key={i} text={r.text} />)
        )}
      </div>
    </div>
  );
}
